import { ReactNode, SyntheticEvent, useContext, useEffect, useState } from "react"
import commonStyles from "../../common.module.css"
import { Button } from "primereact/button"
import { InputNumber, InputNumberChangeParams } from "primereact/inputnumber"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { CSVOrderImportKey } from "../../../api/types/constants"
import { classNames } from "primereact/utils"
import { useConstants } from "../../../api/queries/useConstants"
import { useSettings } from "../../../api/queries/useSettings"
import FieldError, { FormErrors } from "../../FieldError"
import { useUpdateSettings } from "../../../api/queries/useUpdateSettings"
import { CSVOrderImport, CSVOrderImportPositions, UpdateSettingsPayload } from "../../../api/types/settings"
import { validateSettings } from "./validate"
import globalToastContext from "../../../context/globalToast"

type Section = keyof CSVOrderImportPositions

export const Import = () => {
  const { data: constants } = useConstants()
  const { data: settings } = useSettings()
  const updateSettings = useUpdateSettings()
  const { ref } = useContext(globalToastContext)

  const [separator, setSeparator] = useState<string>("")
  const handleSeparatorChange = (event: DropdownChangeParams) => setSeparator(event.target.value)

  const [encoding, setEncoding] = useState<string>("")
  const handleEncodingChange = (event: DropdownChangeParams) => setEncoding(event.target.value)

  const [ignoreRowsNo, setIgnoreRowsNo] = useState<number>(0)
  const handleIgnoreRowsNoChange = (event: InputNumberChangeParams) =>
    setIgnoreRowsNo(event.value || 0)

  const [errors, setErrors] = useState<FormErrors<CSVOrderImport>>({})

  const [positions, setPositions] = useState<CSVOrderImportPositions>({
    loading: [],
    unloading: [],
    goods: [],
    calculation: [],
  })

  const handleValueChange = (section: Section, key: string, position: number | null) => {
    let newSection = [...positions[section]]

    if (position === null) {
      // remove item
      newSection = newSection.filter((item) => item.key !== key)
    } else {
      const item = newSection.find((item) => item.key === key)

      if (item) {
        // update item
        item.position = position
      } else {
        // add item
        newSection.push({ key, position })
      }
    }

    const newPayload = Object.assign({}, positions)
    newPayload[section] = newSection
    setPositions(newPayload)
  }

  const tValue = (section: Section, key: string): ReactNode => {
    const item = positions[section].find((item) => item.key === key)
    const position = item ? item.position : null

    const allPositions = [
      ...positions.loading,
      ...positions.unloading,
      ...positions.goods,
      ...positions.calculation,
    ]

    const isInvalid = position
      ? allPositions.filter((i) => i.position === position).length > 1
      : false

    return (
      <InputNumber
        value={position}
        onChange={(e) => handleValueChange(section, key, e.value)}
        min={0}
        className={classNames({ "p-invalid": isInvalid })}
      />
    )
  }

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    const payload: UpdateSettingsPayload = {
      CSVOrderImport: {
        encoding,
        separator,
        ignoreRowsNo,
        positions,
      },
    }

    const [isValid, errors] = validateSettings(payload.CSVOrderImport)

    if (isValid) {
      await updateSettings.mutateAsync(payload)

      ref?.current?.show({ severity: "success", summary: "Změny uloženy." })
    } else {
      setErrors(errors)
    }
  }

  useEffect(() => {
    if (settings) {
      setSeparator(settings.results.CSVOrderImport.separator)
      setEncoding(settings.results.CSVOrderImport.encoding)
      setIgnoreRowsNo(settings.results.CSVOrderImport.ignoreRowsNo || 0)
      setPositions(settings.results.CSVOrderImport.positions)
    }
  }, [settings])

  return (
    <form className={"col-12"} onSubmit={handleFormSubmit}>
      <div className={"p-fluid"}>
        <div className={"block-title block-title_blue border-round-top px-3 py-2"}>
          Nastavení CSV importu objednávek
        </div>

        <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
          <div className={"grid"}>
            <div className={"field col-12 md:col-3"}>
              <span className={"p-float-label"}>
                <Dropdown options={constants?.results.CSVSeparators} value={separator}
                  onChange={handleSeparatorChange}
                  className={classNames({ "p-invalid": !!errors?.separator })}
                />
                <label className={commonStyles.labelRequired}>Oddělovač:</label>
              </span>
              <FieldError error={errors?.separator} />
            </div>

            <div className={"field col-12 md:col-3"}>
              <span className={"p-float-label"}>
                <Dropdown options={constants?.results.encodings} value={encoding}
                  onChange={handleEncodingChange}
                  className={classNames({ "p-invalid": !!errors?.encoding })}
                />
                <label className={commonStyles.labelRequired}>Kódování:</label>
              </span>
              <FieldError error={errors?.encoding} />
            </div>

            <div className={"field col-12 md:col-3"}>
              <span className={"p-float-label"}>
                <InputNumber value={ignoreRowsNo} min={0}
                  onChange={handleIgnoreRowsNoChange}
                  className={classNames({ "p-invalid": !!errors?.ignoreRowsNo })}
                />
                <label className={commonStyles.labelRequired}>Přeskočit prvních řádků:</label>
              </span>
              <FieldError error={errors?.ignoreRowsNo} />
            </div>
          </div>
        </div>

        <div className={"block-title block-title_blue border-round-top px-3 py-2"}>
          Nakládka
        </div>

        <div className={"border-solid border-1 border-300 p-3 mb-6"}>
          <DataTable size="small" value={constants?.results.CSVOrderImportKeys.loading}
            stripedRows={true}>
            <Column field="label" header="Název" />
            <Column field="value" header="Pozice v souboru"
              body={(row: CSVOrderImportKey) => tValue("loading", row.value)} />
          </DataTable>
        </div>

        <div className={"block-title block-title_blue border-round-top px-3 py-2"}>
          Vykládka
        </div>

        <div className={"border-solid border-1 border-300 p-3 mb-6"}>
          <DataTable size="small" value={constants?.results.CSVOrderImportKeys.unloading}
            stripedRows={true}>
            <Column field="label" header="Název" />
            <Column field="value" header="Pozice v souboru"
              body={(row: CSVOrderImportKey) => tValue("unloading", row.value)} />
          </DataTable>
        </div>

        <div className={"block-title block-title_blue border-round-top px-3 py-2"}>
          Zásilka
        </div>

        <div className={"border-solid border-1 border-300 p-3 mb-6"}>
          <DataTable size="small" value={constants?.results.CSVOrderImportKeys.goods}
            stripedRows={true}>
            <Column field="label" header="Název" />
            <Column field="value" header="Pozice v souboru"
              body={(row: CSVOrderImportKey) => tValue("goods", row.value)} />
          </DataTable>
        </div>

        <div className={"block-title block-title_blue border-round-top px-3 py-2"}>
          Kalkulace
        </div>

        <div className={"border-solid border-1 border-300 p-3 mb-6"}>
          <DataTable size="small" value={constants?.results.CSVOrderImportKeys.calculation}
            stripedRows={true}>
            <Column field="label" header="Název" />
            <Column field="value" header="Pozice v souboru"
              body={(row: CSVOrderImportKey) => tValue("calculation", row.value)} />
          </DataTable>
        </div>
      </div>

      <div className={"text-right"}>
        <Button type={"submit"} label={"Uložit"}
          className={"p-button p-button-sm p-button-success"}
          disabled={updateSettings.isLoading}
          icon={"pi pi-save"} />
      </div>
    </form>
  )
}
